import { BoxV2 as Box, Flex } from 'portal-commons';
import { FunctionComponent, useEffect, useMemo } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { faMessageDots, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Grid } from '@material-ui/core';

import HeadingBlock from './HeadingBlock';
import StyledTextField from './StyledTextField';
import MaskedContainer from '../MaskedContainer';
import { CampaignDetailsFormValues } from '../../types';
import { charCounter, isFieldGreyOut } from '../../utils';
import {
  createMaxLengthValidator,
  createMinLengthValidator,
  createRequiredValidator,
  isValidInput,
} from '../../validators';

interface SampleMessagesSectionProp {
  greyOutFields: string[];
  minimumCount: number;
}

const SampleMessagesSection: FunctionComponent<SampleMessagesSectionProp> = ({
  greyOutFields,
  minimumCount,
}) => {
  const { control } = useFormContext<CampaignDetailsFormValues>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'sampleMessages',
  });
  const disabled = useMemo(
    () => isFieldGreyOut(greyOutFields, 'sampleMessage'),
    [greyOutFields]
  );

  useEffect(() => {
    if (fields.length < minimumCount) {
      const count = minimumCount - fields.length;
      for (let i = 0; i < count; i++) {
        append({ message: '' });
      }
    }
  }, [minimumCount]);

  return (
    <Grid item xs={12} data-testid="campaignDetailsFormSampleMessages">
      <MaskedContainer className="sample-messages wrapper" masked={disabled}>
        <HeadingBlock icon={faMessageDots} title="Sample Messages" />
        <Grid container spacing={4}>
          {fields.map((field, index) => (
            <Grid
              item
              xs={6}
              key={field.id}
              className={
                fields.length % 2 === 0 ||
                (fields.length % 2 !== 0 && index < fields.length - 1)
                  ? 'message-grid'
                  : ''
              }
            >
              <div className="form-group-field sample-message">
                <label>
                  <Flex sx={{ alignItems: 'self-end' }}>
                    {`Sample message ${index + 1}`}
                    {fields.length > minimumCount && (
                      <Box sx={{ mr: 'xs' }}>
                        <FontAwesomeIcon
                          icon={faTrash}
                          style={{ cursor: 'pointer' }}
                          color="#0091B3"
                          size="lg"
                          onClick={(e) => {
                            remove(index);
                          }}
                        />
                      </Box>
                    )}
                  </Flex>
                </label>
                <Controller
                  control={control}
                  name={`sampleMessages.${index}`}
                  disabled={disabled}
                  rules={{
                    validate: {
                      isValidInput: (value) => isValidInput(value.message),
                      isRequired: (value) =>
                        createRequiredValidator(
                          'Please type any Sample message'
                        )(value.message),
                      withValidMinLength: (value) =>
                        createMinLengthValidator(20)(value.message),
                      withValidMaxLength: (value) =>
                        createMaxLengthValidator(1024)(value.message),
                    },
                  }}
                  render={({
                    field: { value, onChange, onBlur },
                    fieldState: { error },
                  }) => (
                    <>
                      <StyledTextField
                        data-testid="campaignDetailsFormSampleMessagesDescriptionInput"
                        id="description"
                        inputProps={{
                          'aria-label': 'description',
                          maxLength: 1024,
                        }}
                        fullWidth
                        multiline
                        error={!!error?.message}
                        rows={3}
                        rowsMax={3}
                        value={value.message}
                        helperText={charCounter({
                          value: value.message,
                          maxLength: 1024,
                        })}
                        onChange={(event) =>
                          onChange({ message: event.target.value })
                        }
                        onBlur={onBlur}
                      />
                      {error?.message && (
                        <h6 className="error-msg">{error.message}</h6>
                      )}
                    </>
                  )}
                />
              </div>
            </Grid>
          ))}
          {fields.length < 5 && (
            <Grid
              item
              xs={fields.length % 2 ? 6 : 12}
              style={{ alignSelf: 'center' }}
            >
              <div className="add-message-btn text-center">
                <p>
                  <a
                    data-testid="campaignDetailsFormSampleMessagesAddButton"
                    className="primary-btn"
                    onClick={() => append({ message: '' })}
                  >
                    + Add Sample Message
                  </a>
                </p>
              </div>
            </Grid>
          )}
        </Grid>
      </MaskedContainer>
    </Grid>
  );
};

export default SampleMessagesSection;
